import { useEffect, useState } from 'react';

import {
  CLIPBOARD_COPY_SUCCESS_EVENT_NAME,
  type ClipboardCopySuccessEventDetail,
} from '@/lib/clipboard-copy-success-event';
import { cn } from '@/lib/utils';

type CopySuccessToastState = {
  copied_text: string;
  is_visible: boolean;
};

const COPY_SUCCESS_TOAST_HIDE_DELAY_MS = 1600;
const COPY_SUCCESS_TOAST_CLASS_NAME =
  'pointer-events-none fixed left-1/2 top-16 z-[60] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-full bg-[#26221c]/90 px-4 py-2 text-sm font-medium tracking-normal text-white shadow-[0_8px_24px_rgba(0,0,0,0.2)] transition-all duration-300 ease-out';
const COPY_SUCCESS_TOAST_VISIBLE_CLASS_NAME = 'translate-y-0 opacity-100';
const COPY_SUCCESS_TOAST_HIDDEN_CLASS_NAME = '-translate-y-1 opacity-0';

export function CopySuccessToast() {
  const [copy_success_toast_state, set_copy_success_toast_state] =
    useState<CopySuccessToastState>({
      copied_text: '',
      is_visible: false,
    });

  useEffect(() => {
    let hide_toast_timeout_id: number | null = null;

    const clear_hide_toast_timeout = () => {
      if (!hide_toast_timeout_id) {
        return;
      }

      window.clearTimeout(hide_toast_timeout_id);
      hide_toast_timeout_id = null;
    };

    const handle_clipboard_copy_success_event = (event: Event) => {
      const copied_text = get_clipboard_copy_success_text(event);

      if (!copied_text) {
        return;
      }

      clear_hide_toast_timeout();
      set_copy_success_toast_state({
        copied_text,
        is_visible: true,
      });
      hide_toast_timeout_id = window.setTimeout(() => {
        set_copy_success_toast_state(copy_success_toast_state => ({
          ...copy_success_toast_state,
          is_visible: false,
        }));
      }, COPY_SUCCESS_TOAST_HIDE_DELAY_MS);
    };

    window.addEventListener(
      CLIPBOARD_COPY_SUCCESS_EVENT_NAME,
      handle_clipboard_copy_success_event,
    );

    return () => {
      clear_hide_toast_timeout();
      window.removeEventListener(
        CLIPBOARD_COPY_SUCCESS_EVENT_NAME,
        handle_clipboard_copy_success_event,
      );
    };
  }, []);

  return (
    <div
      aria-live="polite"
      className={get_copy_success_toast_class_name(
        copy_success_toast_state.is_visible,
      )}
      role="status"
    >
      已复制：{copy_success_toast_state.copied_text}
    </div>
  );
}

function get_clipboard_copy_success_text(event: Event) {
  const copy_success_event =
    event as CustomEvent<ClipboardCopySuccessEventDetail>;

  if (typeof copy_success_event.detail?.copied_text !== 'string') {
    return null;
  }

  return copy_success_event.detail.copied_text;
}

function get_copy_success_toast_class_name(is_visible: boolean) {
  return cn(
    COPY_SUCCESS_TOAST_CLASS_NAME,
    is_visible ?
      COPY_SUCCESS_TOAST_VISIBLE_CLASS_NAME
    : COPY_SUCCESS_TOAST_HIDDEN_CLASS_NAME,
  );
}
